/**
 * La pagina che gira sul monitor in gelateria.
 *
 * Vive appesa a un muro e nessuno la guarda da vicino: tutto qui e' pensato per
 * essere letto da tre metri, e per non aver bisogno di nessuno. Non fa login —
 * la chiave e' il token nell'indirizzo, che dice anche di quale punto vendita
 * si tratta — e non ha comandi: non c'e' nessuno che possa toccarla.
 *
 * Due cose che la rendono adatta a restare accesa per mesi:
 *
 *  - se la rete cade continua a mostrare l'ultima lista buona, presa dalla
 *    memoria del browser, invece di diventare una schermata bianca. Un monitor
 *    vuoto in vetrina e' peggio di un monitor con i gusti di dieci minuti fa;
 *  - le categorie scorrono a rotazione, come le pagine che oggi girano su
 *    Yodeck, cosi' anche con trenta gusti nessuna scritta diventa illeggibile.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';

/** Ogni quanto si richiedono i gusti al server. */
const INTERVALLO_AGGIORNAMENTO = 30000;
/** Quanto resta a schermo una pagina, se non lo dice nessun altro. */
const DURATA_PAGINA_PREDEFINITA = 15;
/** Da quanto tempo i dati devono essere fermi perche' valga la pena dirlo. */
const SOGLIA_DATI_VECCHI = 10 * 60 * 1000;

interface GustoMonitor {
  id: number;
  nome: string;
  nome_secondario: string | null;
  colore: string;
  foto_url: string | null;
  stagionale: boolean;
}

interface MediaMonitor {
  url: string;
  tipo: 'immagine' | 'video';
}

interface PaginaMonitor {
  tipo: 'gusti' | 'media';
  titolo: string | null;
  secondi: number;
  sfondo?: MediaMonitor | null;
  media?: MediaMonitor | null;
  gusti?: GustoMonitor[];
}

interface DatiMonitor {
  schermo: { nome: string; puntoVendita: string | null };
  aggiornatoAl: string;
  totale: number;
  secondiPagina: number;
  /** Se i due nomi del gusto si alternano sotto la foto. */
  alternaNomi: boolean;
  secondiNome: number;
  /** Ingrandimento generale, in percentuale. */
  scala: number;
  /** Quanto togliere in alto e ai lati per stare dentro l'arco, in percentuale. */
  margineAlto: number;
  margineLati: number;
  /** Quanto il velo copre lo sfondo, in percentuale. */
  veloSfondo: number;
  /** Griglia fissata a mano: 0 vuol dire "calcolala tu". */
  colonne: number;
  righe: number;
  /** Distanza fra i gusti, in percentuale del valore di riferimento. */
  spazioVerticale: number;
  spazioOrizzontale: number;
  pagine: PaginaMonitor[];
}

const StiliMonitor = createGlobalStyle`
  /* Il monitor e' l'unica cosa sullo schermo: niente barre, niente margini */
  html, body, #root {
    height: 100%;
    margin: 0;
    background: #fdf6e3;
    overflow: hidden;
  }
`;

const Schermo = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fdf6e3;
  color: #2b2b2b;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  overflow: hidden;
`;

/**
 * Lo sfondo sta dietro tutto e non deve mai rubare la scena: sopra ci va un
 * velo chiaro, altrimenti i nomi dei gusti diventano illeggibili appena il
 * video schiarisce.
 */
const Sfondo = styled.div<{ $velo: number }>`
  position: absolute;
  inset: 0;
  overflow: hidden;
  z-index: 0;

  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(253, 246, 227, ${(p) => p.$velo});
  }
`;

/** Una pagina di solo contenuto: il file riempie lo schermo, senza velo. */
const MediaPieno = styled.div`
  position: absolute;
  inset: 0;
  background: #000;
  z-index: 1;

  img, video {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

/**
 * L'area dentro cui si puo' disegnare.
 *
 * Lo schermo della gelateria e' incassato in un arco e i due angoli in alto
 * sono coperti dalla muratura: quello che ci finisce sotto non lo legge
 * nessuno. Quanto togliere non si puo' indovinare da qui, lo decide chi sta
 * davanti allo schermo.
 */
const Contenuto = styled.div<{ $alto: number; $lati: number }>`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: ${(p) => p.$alto}vh ${(p) => p.$lati}vw 0;
`;

const comparsa = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: none; }
`;

const Titolo = styled.h1<{ $scala: number }>`
  font-size: clamp(1.6rem, ${(p) => 6 * p.$scala}vh, 5rem);
  /* Un titolo lungo non deve arrivare agli angoli, dove c'e' l'arco */
  max-width: 82%;
  text-align: center;
  margin: 0 0 4vh;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #e8a33d;
  animation: ${comparsa} 0.6s ease both;
`;

/**
 * Le colonne: quelle impostate a mano, o calcolate per riempire le righe in
 * modo pari.
 *
 * Nel calcolo automatico cinque gusti diventano 3+2, non 4+1: un gusto solo
 * sulla seconda riga sembra un errore. Il massimo di quattro colonne tiene i
 * cerchi grandi abbastanza da leggersi da lontano; se i gusti sono di piu', la
 * pagina viene divisa a monte.
 *
 * Chi imposta la griglia a mano sta davanti allo schermo e vede cose che noi da
 * qui non sappiamo — quanto e' lungo il nome piu' lungo, quanto mangia l'arco —
 * quindi la sua scelta vince, ma senza mai lasciare colonne vuote: se i gusti
 * in pagina sono meno delle colonne impostate, si usano quelli.
 */
const colonnePer = (quanti: number, impostate: number): number => {
  if (quanti <= 1) return 1;
  if (impostate > 0) return Math.min(impostate, quanti);
  const righe = Math.ceil(quanti / 4);
  return Math.ceil(quanti / righe);
};

const Griglia = styled.div<{
  $quanti: number; $scala: number; $colonne: number;
  $spazioV: number; $spazioO: number;
}>`
  display: grid;
  grid-template-columns: repeat(${(p) => colonnePer(p.$quanti, p.$colonne)}, 1fr);
  gap: ${(p) => 3 * p.$scala * p.$spazioV}vh ${(p) => 2 * p.$scala * p.$spazioO}vw;
  max-width: 100%;
  animation: ${comparsa} 0.6s ease both;
`;

const sfuma = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Gusto = styled.div<{ $scala: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.4vh;
  text-align: center;

  .foto, .senza-foto {
    width: clamp(80px, ${(p) => 15 * p.$scala}vh, 320px);
    height: clamp(80px, ${(p) => 15 * p.$scala}vh, 320px);
    border-radius: 50%;
    object-fit: cover;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
  }

  .nome {
    font-size: clamp(0.85rem, ${(p) => 2.4 * p.$scala}vh, 2.6rem);
    font-weight: 700;
    text-transform: uppercase;
    line-height: 1.15;
    max-width: ${(p) => 16 * p.$scala}vw;
    /* Lo spazio resta occupato anche quando il nome cambia lunghezza: senza,
       la griglia si muoverebbe a ogni cambio di lingua */
    min-height: 2.4em;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    animation: ${sfuma} 0.5s ease;
  }
`;

const PieDiPagina = styled.footer`
  position: absolute;
  bottom: 2vh;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5vw;
  font-size: 1.6vh;
  color: rgba(0, 0, 0, 0.35);
`;

const Pallino = styled.span<{ $attivo: boolean }>`
  width: 1.2vh;
  height: 1.2vh;
  border-radius: 50%;
  background: ${(p) => (p.$attivo ? '#e8a33d' : 'rgba(0,0,0,0.15)')};
  display: inline-block;
`;

const Avviso = styled.div`
  position: absolute;
  top: 2vh;
  right: 2vw;
  padding: 0.8vh 1.4vw;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.08);
  font-size: 1.5vh;
  color: rgba(0, 0, 0, 0.5);
`;

const Centrato = styled.div`
  text-align: center;
  font-size: 3vh;
  color: rgba(0, 0, 0, 0.45);
  padding: 0 10vw;
`;

/** Dove si tiene l'ultima lista buona, per sopravvivere a una rete che cade. */
const chiaveMemoria = (token: string) => `monitor-${token}`;

/** Ogni quanto si controlla se e' uscita una versione nuova della pagina. */
const INTERVALLO_VERSIONE = 5 * 60 * 1000;

/**
 * Si ricarica da solo quando il codice pubblicato cambia.
 *
 * Il monitor e' un browser acceso su una pagina che nessuno tocca per
 * settimane. I dati si aggiornano da soli — quelli si richiedono ogni trenta
 * secondi — ma il **codice** resta quello caricato il giorno dell'accensione:
 * dopo un rilascio, lo schermo continua a girare la versione vecchia e ogni
 * regolazione nuova sembra non fare niente, perche' quella pagina non sa che
 * esiste. E' successo esattamente cosi' con colonne e distanze.
 *
 * `asset-manifest.json` cambia nome al file principale a ogni build: se il nome
 * non e' piu' quello con cui questa pagina e' partita, si ricarica. Un errore
 * di rete non fa niente — meglio una versione vecchia che uno schermo bianco.
 */
const useAggiornamentoAutomatico = () => {
  const versioneIniziale = useRef<string | null>(null);

  useEffect(() => {
    let annullato = false;

    const controlla = async () => {
      try {
        const risposta = await fetch('/asset-manifest.json', { cache: 'no-store' });
        if (!risposta.ok) return;
        const manifest = await risposta.json();
        const attuale = manifest?.files?.['main.js'];
        if (annullato || typeof attuale !== 'string') return;

        if (versioneIniziale.current === null) {
          versioneIniziale.current = attuale;
          return;
        }
        if (attuale !== versioneIniziale.current) window.location.reload();
      } catch {
        // rete assente o manifest non servito: si continua com'e'
      }
    };

    controlla();
    const timer = setInterval(controlla, INTERVALLO_VERSIONE);
    return () => { annullato = true; clearInterval(timer); };
  }, []);
};

const MonitorPage: React.FC<{ token: string }> = ({ token }) => {
  const [dati, setDati] = useState<DatiMonitor | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [indice, setIndice] = useState(0);
  const [ultimoContatto, setUltimoContatto] = useState<Date | null>(null);
  const indiceRef = useRef(0);

  useAggiornamentoAutomatico();

  // All'avvio si parte da quello che si ricorda: se il monitor si riaccende
  // mentre la rete non c'e' ancora, mostra i gusti invece del vuoto.
  useEffect(() => {
    try {
      const salvato = localStorage.getItem(chiaveMemoria(token));
      if (salvato) setDati(JSON.parse(salvato));
    } catch {
      // memoria non disponibile: si prosegue senza
    }
  }, [token]);

  const carica = useCallback(async () => {
    try {
      const risposta = await fetch(`/api/monitor/schermo/${encodeURIComponent(token)}`);
      if (risposta.status === 404) {
        setErrore('Schermo non riconosciuto');
        return;
      }
      if (!risposta.ok) throw new Error('risposta non valida');

      const nuovi: DatiMonitor = await risposta.json();
      setDati(nuovi);
      setUltimoContatto(new Date());
      setErrore(null);
      try {
        localStorage.setItem(chiaveMemoria(token), JSON.stringify(nuovi));
      } catch {
        // niente memoria: pazienza, si perde solo la resistenza ai black-out
      }
    } catch {
      // Non si cancella niente: resta a schermo l'ultima lista buona
      setErrore('rete');
    }
  }, [token]);

  useEffect(() => {
    carica();
    const timer = setInterval(carica, INTERVALLO_AGGIORNAMENTO);
    return () => clearInterval(timer);
  }, [carica]);

  /**
   * Quale dei due nomi mostrare adesso.
   *
   * Non e' una traduzione a runtime: e' il nome che qualcuno ha scritto nella
   * scheda del gusto. Un gusto senza secondo nome mostra sempre il suo, senza
   * buchi e senza aspettare il turno.
   */
  const [secondaLingua, setSecondaLingua] = useState(false);

  useEffect(() => {
    if (!dati?.alternaNomi) {
      setSecondaLingua(false);
      return;
    }
    const timer = setInterval(
      () => setSecondaLingua((precedente) => !precedente),
      Math.max(2, dati.secondiNome || 5) * 1000
    );
    return () => clearInterval(timer);
  }, [dati?.alternaNomi, dati?.secondiNome]);

  const nomeDa = (gusto: GustoMonitor) =>
    (secondaLingua && gusto.nome_secondario) ? gusto.nome_secondario : gusto.nome;

  // Scala e velo arrivano in percentuale e si usano come frazioni
  const scala = (dati?.scala ?? 100) / 100;
  const velo = (dati?.veloSfondo ?? 78) / 100;
  const spazioV = (dati?.spazioVerticale ?? 100) / 100;
  const spazioO = (dati?.spazioOrizzontale ?? 100) / 100;

  const pagine = dati?.pagine ?? [];
  // Se una pagina sparisce mentre e' a schermo, si rientra nei limiti
  const corrente: PaginaMonitor | undefined = pagine[Math.min(indice, Math.max(pagine.length - 1, 0))];

  /**
   * Il passaggio alla pagina successiva.
   *
   * Ogni pagina dura quanto dice lei: un video promozionale resta il tempo che
   * serve a finirlo, una pagina di gusti quanto e' stato impostato. Il timer si
   * rifa' a ogni cambio, altrimenti pagine di durata diversa si accavallano.
   */
  useEffect(() => {
    if (pagine.length <= 1) return;
    const durata = (corrente?.secondi || dati?.secondiPagina || DURATA_PAGINA_PREDEFINITA) * 1000;
    const timer = setTimeout(() => {
      indiceRef.current = (indiceRef.current + 1) % pagine.length;
      setIndice(indiceRef.current);
    }, durata);
    return () => clearTimeout(timer);
  }, [pagine.length, indice, corrente, dati]);

  const datiVecchi = ultimoContatto
    ? Date.now() - ultimoContatto.getTime() > SOGLIA_DATI_VECCHI
    : false;

  return (
    <>
      <StiliMonitor />
      <Schermo>
        {errore === 'Schermo non riconosciuto' && (
          <Centrato>
            Questo schermo non è riconosciuto.<br />
            Controlla l'indirizzo nelle impostazioni dei monitor.
          </Centrato>
        )}

        {errore !== 'Schermo non riconosciuto' && !corrente && (
          <Centrato>Nessun gusto in vetrina in questo momento.</Centrato>
        )}

        {corrente && corrente.tipo === 'media' && corrente.media && (
          <MediaPieno key={`media-${indice}`}>
            {corrente.media.tipo === 'video'
              ? (
                <video src={corrente.media.url} autoPlay muted loop playsInline />
              ) : (
                <img src={corrente.media.url} alt="" />
              )}
          </MediaPieno>
        )}

        {corrente && corrente.tipo === 'gusti' && (
          <>
            {corrente.sfondo && (
              <Sfondo key={`sfondo-${corrente.sfondo.url}`} $velo={velo}>
                {corrente.sfondo.tipo === 'video'
                  ? <video src={corrente.sfondo.url} autoPlay muted loop playsInline />
                  : <img src={corrente.sfondo.url} alt="" />}
              </Sfondo>
            )}

            <Contenuto $alto={dati?.margineAlto ?? 0} $lati={dati?.margineLati ?? 0}>
              <Titolo $scala={scala} key={corrente.titolo || indice}>{corrente.titolo}</Titolo>
              <Griglia
                $quanti={(corrente.gusti || []).length}
                $scala={scala}
                $colonne={dati?.colonne ?? 0}
                $spazioV={spazioV}
                $spazioO={spazioO}
                key={`griglia-${indice}`}
              >
                {(corrente.gusti || []).map((gusto) => (
                  <Gusto key={gusto.id} $scala={scala}>
                    {gusto.foto_url
                      ? <img className="foto" src={gusto.foto_url} alt="" />
                      : <div className="senza-foto" style={{ backgroundColor: gusto.colore }} />}
                    <span className="nome" key={nomeDa(gusto)}>{nomeDa(gusto)}</span>
                  </Gusto>
                ))}
              </Griglia>
            </Contenuto>
          </>
        )}

        {pagine.length > 1 && (
          <PieDiPagina>
            {pagine.map((pagina, i) => (
              <Pallino key={`${pagina.tipo}-${i}`} $attivo={i === indice} />
            ))}
          </PieDiPagina>
        )}

        {/* Si avvisa solo se il distacco dura: un secondo di rete ballerina non
            deve mettere una scritta in vetrina */}
        {(errore === 'rete' && datiVecchi) && (
          <Avviso>Aggiornamento non riuscito</Avviso>
        )}
      </Schermo>
    </>
  );
};

export default MonitorPage;
